import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  Send,
  CheckCircle,
  Building,
  Users,
} from "lucide-react";
import { useJob } from "../context/JobContext";
import { Link } from "react-router-dom";
import defaultJobs from "./defaultJobs";

const processSteps = [
  {
    number: "01",
    title: "Submit Your Job",
    description:
      "Fill out our detailed job submission form with your requirements and preferences.",
  },
  {
    number: "02",
    title: "Expert Matching",
    description:
      "Our recruitment specialists handpick candidates that match your specific criteria.",
  },
  {
    number: "03",
    title: "Talent Acquisition",
    description:
      "Receive a curated list of pre-vetted professionals ready to fill your position.",
  },
];
const JobSubmissionPage = () => {
  const { createJob, submitJobLoading } = useJob();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    companyName: "",
    location: "",
    type: "full-time",
    industry: "Technology",
    experienceLevel: "entry",
    salary: "",
    description: "",
    responsibilities: "",
    requirements: "",
    benefits: "",
    skills: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    deadline: "",
  });

  const jobTypes = [
    { value: "full-time", label: "Full Time" },
    { value: "part-time", label: "Part Time" },
    { value: "contract", label: "Contract" },
    { value: "temporary", label: "Temporary" },
    { value: "internship", label: "Internship" },
  ];

  const experienceLevels = [
    { value: "entry", label: "Entry Level" },
    { value: "junior", label: "Junior (1-3 years)" },
    { value: "mid", label: "Mid Level (3-5 years)" },
    { value: "senior", label: "Senior (5+ years)" },
    { value: "executive", label: "Executive" },
  ];

  const currencies = [
    { value: "USD", label: "USD ($)" },
    { value: "EUR", label: "EUR (€)" },
    { value: "GBP", label: "GBP (£)" },
    { value: "CAD", label: "CAD (C$)" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Helper to get jobs from localStorage
  function getLocalStorageJobs() {
    try {
      const stored = localStorage.getItem("techmind_jobs");
      return [...(stored ? JSON.parse(stored) : [])];
    } catch (err) {
      console.error("Error retrieving jobs from storage:", err);
      return [];
    }
  }
    // Helper to save jobs to localStorage
    function saveJobsToLocalStorage(jobs) {
      try {
        localStorage.setItem("techmind_jobs", JSON.stringify(jobs));
        return true;
      } catch (err) {
        console.error("Error saving jobs to storage:", err);
        return false;
      }
    }

      // Flags for countries
  const flags = {
    "United States":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='3.08' y='0' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='6.15' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='12.31' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='18.46' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='24.62' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='30.77' fill='%23d80027'/%3E%3Crect width='60' height='3.08' y='36.92' fill='%23d80027'/%3E%3Crect width='24' height='20.77' fill='%232e4b80'/%3E%3C/svg%3E",
    "United Kingdom":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23012169'/%3E%3Cpath d='M0,0 L60,40 M60,0 L0,40' stroke='%23fff' stroke-width='6'/%3E%3Cpath d='M30,0 L30,40 M0,20 L60,20' stroke='%23fff' stroke-width='10'/%3E%3Cpath d='M30,0 L30,40 M0,20 L60,20' stroke='%23c8102e' stroke-width='6'/%3E%3Cpath d='M0,0 L60,40 M60,0 L0,40' stroke='%23c8102e' stroke-width='2'/%3E%3C/svg%3E",
    India:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='13.33' y='0' fill='%23ff9933'/%3E%3Crect width='60' height='13.33' y='26.67' fill='%23138808'/%3E%3Ccircle cx='30' cy='20' r='4' fill='%23000080'/%3E%3C/svg%3E",
    "United Arab Emirates":
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='20' height='40' fill='%23009e49'/%3E%3Crect width='60' height='13.33' y='0' fill='%23ce1126'/%3E%3Crect width='60' height='13.33' y='26.67' fill='%23000'/%3E%3C/svg%3E",
    Singapore:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 40'%3E%3Crect width='60' height='40' fill='%23f0f0f0'/%3E%3Crect width='60' height='20' fill='%23ed2939'/%3E%3Ccircle cx='16' cy='20' r='6' fill='%23f0f0f0'/%3E%3Ccircle cx='20' cy='20' r='6' fill='%23f0f0f0'/%3E%3Cpath d='M11,16 L25,24 M25,16 L11,24' stroke='%23ed2939' stroke-width='1'/%3E%3C/svg%3E",
  };
  // Helper to add a job to localStorage
  function addJobToLocalStorage(job) {
    try {
      const jobs = getLocalStorageJobs();

      const newId = Math.max(0, ...jobs.map(j => j.id || 0)) + 7;
      const newJob = {
        ...job,
        id: newId,
        posted: "Just now",
        type: job.employmentType || "Full-time",
        skills: typeof job.skills === "string" ? job.skills.split(",").map(s => s.trim()) : (job.skills || []),
        flag: flags[job.location] || flags["United States"],
      };
      const updatedJobs = [newJob, ...jobs];
      saveJobsToLocalStorage(updatedJobs);
      return newJob;
    } catch (err) {
      console.error("Error adding job to storage:", err);
      return null;
    }
  }
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Map formData to API payload matching PHP requirements
    const payload = {
      title: formData.title,
      company: formData.company,
      companyName: formData.companyName || formData.company,
      location: formData.location,
      type: formData.type,
      industry: formData.industry,
      experienceLevel: formData.experienceLevel,
      salary: formData.salary,
      description: formData.description,
      responsibilities: formData.responsibilities
        ? formData.responsibilities.split("\n").filter((line) => line.trim())
        : [],
      requirements: formData.requirements
        ? formData.requirements.split("\n").filter((line) => line.trim())
        : [],
      benefits: formData.benefits
        ? formData.benefits.split("\n").filter((line) => line.trim())
        : [],
      skills: formData.skills
        ? formData.skills
            .split(",")
            .map((skill) => skill.trim())
            .filter((skill) => skill)
        : [],
      contactName: formData.contactName,
      email: formData.contactEmail,
      phone: formData.contactPhone,
      deadline: formData.deadline || null,
    };

    try {
      addJobToLocalStorage(payload)
      // await createJob(payload);
      setIsSubmitted(true);

      setFormData({
        title: "",
        company: "",
        companyName: "",
        location: "",
        type: "full-time",
        industry: "Technology",
        experienceLevel: "entry",
        salary: "",
        description: "",
        responsibilities: "",
        requirements: "",
        benefits: "",
        skills: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        deadline: "",
      })
    } catch (error) {
      console.error("Error submitting job:", error);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white p-8 rounded-xl shadow-lg"
          >
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Job Posted Successfully!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for posting your job opportunity. Our team will review
              it and make it live within 24 hours.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Post Another Job
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative  bg-[#1A193B] pt-20 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              {/* Post a Job */}
              Submit a Job Opening
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              {/* Reach thousands of qualified candidates and find the perfect talent for your organization. */}
              Connect with elite tech talent by submitting your job opening_
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Job Details
              </h2>
              <p className="text-gray-600">
                Fill out the form below to post your job opportunity. All fields
                marked with * are required.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Briefcase className="w-6 h-6 mr-2 text-blue-600" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Job Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Senior Software Engineer"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="companyName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Display Company Name
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company name to display (optional)"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contactName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Person Name
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="HR Manager or Recruiter name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label
                      htmlFor="location"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Location *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="City, State or Remote"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="type"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Job Type *
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {jobTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="industry"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Industry
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Education">Education</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Retail">Retail</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Salary Information */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <DollarSign className="w-6 h-6 mr-2 text-blue-600" />
                  Salary Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="experienceLevel"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Experience Level *
                    </label>
                    <select
                      id="experienceLevel"
                      name="experienceLevel"
                      value={formData.experienceLevel}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="entry">Entry Level</option>
                      <option value="junior">Junior (1-3 years)</option>
                      <option value="mid">Mid Level (3-5 years)</option>
                      <option value="senior">Senior (5+ years)</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="salary"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Salary
                    </label>
                    <input
                      type="text"
                      id="salary"
                      name="salary"
                      value={formData.salary}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., $50,000 - $80,000 or Competitive"
                    />
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Building className="w-6 h-6 mr-2 text-blue-600" />
                  Job Description
                </h3>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Job Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe the role, responsibilities, and what makes this position exciting..."
                  />
                </div>
{/* 
                <div>
                  <label
                    htmlFor="responsibilities"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Key Responsibilities (one per line)
                  </label>
                  <textarea
                    id="responsibilities"
                    name="responsibilities"
                    value={formData.responsibilities}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="• Lead development team\n• Design system architecture\n• Code review and mentoring"
                  />
                </div>

                <div>
                  <label
                    htmlFor="requirements"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Requirements & Qualifications (one per line)
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="• 5+ years experience\n• Bachelor's degree\n• Strong communication skills"
                  />
                </div>

                <div>
                  <label
                    htmlFor="benefits"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Benefits & Perks (one per line)
                  </label>
                  <textarea
                    id="benefits"
                    name="benefits"
                    value={formData.benefits}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="• Health insurance\n• 401k matching\n• Flexible work hours"
                  />
                </div> */}
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Users className="w-6 h-6 mr-2 text-blue-600" />
                  Contact Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="contactEmail"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="hr@company.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contactPhone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      id="contactPhone"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="deadline"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Application Deadline
                  </label>
                  <input
                    type="date"
                    id="deadline"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={submitJobLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  {submitJobLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Posting Job...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Post Job
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <div className="bg-slate-900 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-slate-800 border border-slate-700 rounded-lg p-8 hover:border-slate-600 transition-colors"
              >
                <div className="text-4xl font-bold text-blue-400 mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 p-8 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-xl text-center max-w-4xl mx-auto border border-blue-800/50"
          >
            <h2 className="text-3xl font-bold text-white mb-6">Need Help?</h2>
            <p className="text-blue-100 text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Our recruitment specialists are ready to assist you with any
              questions about submitting your job opening.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <button
                  // onClick={handleContactUs}
                  className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Contact Us
                </button>
              </Link>
              <Link to="/moderator-login">
                <button
                  // onClick={handleModeratorAccess}
                  className="px-8 py-3 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Moderator Access
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default JobSubmissionPage;
